import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Fab,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useAuth } from '../contexts/AuthContext';

const MANUAL_WELCOME = {
  role: 'bot',
  text:
    '¡Hola! Preguntame lo que necesites sobre el sistema — te respondo con lo que dice el manual y te dejo el enlace a la sección.',
};

const CAPRIA_WELCOME = {
  role: 'bot',
  text:
    '¡Hola! Soy **CaprIA**. Puedo armarte un pedido minorista o mayorista (vos lo confirmás en la app) o contarte el estado de un pedido. Por ejemplo: "pedido mayorista para El Toro: 10 kg de chorizo" o "¿cómo viene el pedido 2026-014?".',
};

const currency = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' });

// Render mínimo del markdown que devuelven los asistentes: escapa HTML y
// convierte enlaces a la documentación y negritas. Los enlaces al manual
// se abren en otra pestaña para no perder lo que se estaba haciendo.
const renderAnswer = (text) => {
  let safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  safe = safe.replace(
    /\[([^\]]+)\]\((\/manual\.html#[\w-]+|#[\w-]+)\)/g,
    (m, label, url) => {
      const href = url.startsWith('#') ? `/manual.html${url}` : url;
      return `<a href="${href}" target="_blank" rel="noopener">${label}</a>`;
    }
  );
  safe = safe.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  return safe.replace(/\n/g, '<br>');
};

// Panel de chat compartido por ambas burbujas.
const ChatPanel = ({
  title,
  subtitle,
  placeholder,
  messages,
  sending,
  sendingLabel,
  onClose,
  onSend,
  renderExtra,
}) => {
  const [input, setInput] = useState('');
  const msgsRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    await onSend(text);
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label={title}
      sx={{
        position: 'fixed',
        right: { xs: 0, sm: 16 },
        left: { xs: 0, sm: 'auto' },
        bottom: { xs: 0, sm: 16 },
        width: { xs: '100%', sm: 400 },
        height: { xs: 'min(78dvh, 560px)', sm: 'min(560px, calc(100vh - 64px))' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: { xs: '0.75rem 0.75rem 0 0', sm: '0.75rem' },
        zIndex: (theme) => theme.zIndex.snackbar + 1,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Cerrar asistente">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        ref={msgsRef}
        aria-live="polite"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& a': { color: 'primary.main' },
        }}
      >
        {messages.map((m, i) => (
          <React.Fragment key={i}>
            <Box
              sx={{
                maxWidth: '88%',
                px: 1.5,
                py: 1,
                borderRadius: '0.75rem',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                bgcolor: m.error
                  ? 'error.light'
                  : m.role === 'user'
                    ? 'primary.main'
                    : 'grey.100',
                color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}
              dangerouslySetInnerHTML={{ __html: renderAnswer(m.text) }}
            />
            {renderExtra && renderExtra(m)}
          </React.Fragment>
        ))}
        {sending && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              color: 'text.secondary',
              fontSize: '0.9rem',
            }}
          >
            <CircularProgress size={16} />
            {sendingLabel}
          </Box>
        )}
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          gap: 1,
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 1.5 },
        }}
      >
        <InputBase
          inputRef={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          inputProps={{ maxLength: 1000, 'aria-label': 'Tu mensaje' }}
          sx={{
            flexGrow: 1,
            px: 1.5,
            py: 0.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: '0.5rem',
            // iOS hace zoom al enfocar inputs con fuente < 16px
            fontSize: { xs: 16, sm: '0.9rem' },
          }}
        />
        <IconButton type="submit" color="primary" disabled={sending || !input.trim()} aria-label="Enviar">
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

// Tarjeta del borrador que propone CaprIA. El pedido recién se crea (en estado
// borrador, por los endpoints existentes) cuando el usuario toca el botón, y la
// confirmación final sigue siendo la validación humana de la pantalla de pedido.
const DraftCard = ({ draft, onLoaded }) => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [error, setError] = useState('');

  const handleLoad = async () => {
    setCreating(true);
    setError('');
    try {
      const orderRes = await axios.post('/api/orders/', {
        client: draft.client.id,
        client_id: draft.client.id,
        status: 'borrador',
        order_type: draft.order_type,
        delivery_date: draft.delivery_date,
        special_production: false,
        notes: draft.notes || undefined,
      });
      const order = orderRes.data;
      for (const item of draft.items) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`/api/orders/${order.id}/add_item/`, {
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
        });
      }
      localStorage.setItem('current_order_id', String(order.id));
      setCreatedId(order.id);
      onLoaded();
      navigate(`/orders/${order.id}/edit`);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el borrador. Intentá de nuevo.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ alignSelf: 'flex-start', maxWidth: '95%', width: '100%', p: 1.5, borderRadius: '0.75rem' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle2">{draft.client.name}</Typography>
        <Chip
          size="small"
          color={draft.order_type === 'wholesale' ? 'secondary' : 'primary'}
          label={draft.order_type === 'wholesale' ? 'Mayorista' : 'Minorista'}
        />
      </Box>
      <Divider sx={{ my: 1 }} />
      {draft.items.map((item) => (
        <Box
          key={item.product_id}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '0.85rem', py: 0.25 }}
        >
          <span>
            {item.name} — {item.quantity} {item.unit.toLowerCase()} × {currency.format(item.unit_price)}
          </span>
          <span>{currency.format(item.subtotal)}</span>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 600 }}>
        <span>Total estimado</span>
        <span>{currency.format(draft.total_estimated)}</span>
      </Box>
      {draft.delivery_date && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Entrega: {draft.delivery_date}
        </Typography>
      )}
      {draft.notes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Notas: {draft.notes}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      <Button
        fullWidth
        variant="contained"
        size="small"
        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartCheckoutIcon />}
        disabled={creating || Boolean(createdId)}
        onClick={handleLoad}
        sx={{ mt: 1.5 }}
      >
        {createdId ? 'Borrador cargado' : 'Cargar borrador y revisar'}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
        CaprIA no crea el pedido: vos lo revisás y confirmás en la pantalla de pedido.
      </Typography>
    </Paper>
  );
};

const AssistantHub = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [openChat, setOpenChat] = useState(null); // null | 'manual' | 'capria'
  const [launcherOpen, setLauncherOpen] = useState(false); // burbuja desplegada

  const [manualMessages, setManualMessages] = useState([MANUAL_WELCOME]);
  const [manualSending, setManualSending] = useState(false);
  const [capriaMessages, setCapriaMessages] = useState([CAPRIA_WELCOME]);
  const [capriaSending, setCapriaSending] = useState(false);

  if (!isAuthenticated || location.pathname === '/' || location.pathname === '/login') return null;

  const buildHistory = (messages, welcome) =>
    messages
      .filter((m) => !m.error && m !== welcome)
      .slice(-12)
      .map((m) => ({ role: m.role === 'user' ? 'user' : 'model', text: m.text }));

  const sendManual = async (text) => {
    const history = buildHistory(manualMessages, MANUAL_WELCOME).slice(-8);
    setManualMessages((prev) => [...prev, { role: 'user', text }]);
    setManualSending(true);
    try {
      const res = await axios.post('/api/integrations/assistant/manual/', { question: text, history });
      setManualMessages((prev) => [...prev, { role: 'bot', text: res.data.answer }]);
    } catch (err) {
      const detail =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'No se pudo consultar al asistente. Intentá de nuevo.';
      setManualMessages((prev) => [...prev, { role: 'bot', text: detail, error: true }]);
    } finally {
      setManualSending(false);
    }
  };

  const sendCapria = async (text) => {
    const history = buildHistory(capriaMessages, CAPRIA_WELCOME);
    setCapriaMessages((prev) => [...prev, { role: 'user', text }]);
    setCapriaSending(true);
    try {
      const res = await axios.post('/api/integrations/assistant/capria/', { message: text, history });
      setCapriaMessages((prev) => [
        ...prev,
        { role: 'bot', text: res.data.answer, draft: res.data.draft || null },
      ]);
    } catch (err) {
      const detail =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'No se pudo consultar a CaprIA. Intentá de nuevo.';
      setCapriaMessages((prev) => [...prev, { role: 'bot', text: detail, error: true }]);
    } finally {
      setCapriaSending(false);
    }
  };

  const openFromLauncher = (chat) => {
    setLauncherOpen(false);
    setOpenChat(chat);
  };

  return (
    <>
      {!openChat && (
        <ClickAwayListener onClickAway={() => setLauncherOpen(false)}>
          <Box
            onKeyDown={(e) => { if (e.key === 'Escape') setLauncherOpen(false); }}
            sx={{
              position: 'fixed',
              right: 16,
              bottom: 16,
              zIndex: (theme) => theme.zIndex.snackbar + 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1.5,
              // Cerrado solo ocupa la burbuja: no tapa la paginación ni los botones de la página
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            <Zoom in={launcherOpen} unmountOnExit style={{ transitionDelay: launcherOpen ? '40ms' : '0ms' }}>
              <Fab variant="extended" color="secondary" onClick={() => openFromLauncher('capria')} sx={{ textTransform: 'none' }}>
                <AutoAwesomeIcon sx={{ mr: 1 }} />
                CaprIA
              </Fab>
            </Zoom>
            <Zoom in={launcherOpen} unmountOnExit>
              <Fab variant="extended" color="primary" onClick={() => openFromLauncher('manual')} sx={{ textTransform: 'none' }}>
                <ChatIcon sx={{ mr: 1 }} />
                Asistente
              </Fab>
            </Zoom>
            <Tooltip title={launcherOpen ? '' : 'Asistentes'} placement="left">
              <Fab
                size="small"
                color={launcherOpen ? 'default' : 'primary'}
                aria-label={launcherOpen ? 'Ocultar asistentes' : 'Mostrar asistentes'}
                aria-expanded={launcherOpen}
                onClick={() => setLauncherOpen((v) => !v)}
                sx={{ opacity: launcherOpen ? 1 : 0.85, '&:hover': { opacity: 1 } }}
              >
                {launcherOpen ? <CloseIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />}
              </Fab>
            </Tooltip>
          </Box>
        </ClickAwayListener>
      )}
      {openChat === 'manual' && (
        <ChatPanel
          title="Asistente del manual"
          subtitle="Responde solo con lo que está en la documentación."
          placeholder="Escribí tu pregunta…"
          messages={manualMessages}
          sending={manualSending}
          sendingLabel="Buscando en la documentación…"
          onClose={() => setOpenChat(null)}
          onSend={sendManual}
        />
      )}
      {openChat === 'capria' && (
        <ChatPanel
          title="CaprIA"
          subtitle="Arma pedidos y consulta estados. Vos siempre confirmás."
          placeholder="Ej: pedido minorista para Juan, 3 kg de chorizo…"
          messages={capriaMessages}
          sending={capriaSending}
          sendingLabel="Consultando el sistema…"
          onClose={() => setOpenChat(null)}
          onSend={sendCapria}
          renderExtra={(m) =>
            m.draft ? <DraftCard draft={m.draft} onLoaded={() => setOpenChat(null)} /> : null
          }
        />
      )}
    </>
  );
};

export default AssistantHub;
